import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/lib/i18n";
import AuthLayout from "@/components/layout/AuthLayout";
import { Loader2, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const { t } = useT();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/api/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
      <h1 className="text-2xl font-bold tracking-tight text-slate-900">{t("auth.reset.title")}</h1>
      <p className="mt-1.5 text-sm text-slate-500">{t("auth.reset.subtitle")}</p>

      {done ? (
        <div className="mt-8 space-y-5">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-center" data-testid="reset-success">
            <CheckCircle2 className="mx-auto h-8 w-8 text-primary" />
            <p className="mt-3 text-sm text-emerald-800">{t("auth.reset.done")}</p>
          </div>
          <Link to="/login" className="flex w-full items-center justify-center rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700" data-testid="reset-go-login">
            {t("auth.login.cta")}
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
          {error && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700" data-testid="reset-error">{error}</div>
          )}
          {!token && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-sm text-amber-700">
              Lien invalide ou incomplet.
            </div>
          )}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">{t("auth.password")}</label>
            <input
              type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="reset-password-input" placeholder="8 caractères minimum"
              className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <button
            type="submit" disabled={busy || !token} data-testid="reset-submit-button"
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:opacity-60"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("auth.reset.cta")}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
